import React, { useState, Component } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, XCircle, Check, Crown, TrendingUp } from 'lucide-react';
import { ProgressSteps } from './ProgressSteps';
import { PlanQuestionnaire, InvestorType, DealVolume, TeamSize } from './PlanQuestionnaire';
import { PlanRecommendation } from './PlanRecommendation';
import { recommendPlan, RecommendationResult } from '../../utils/planRecommendation';
import { pricingPlans } from '../../data/pricingPlans';
interface FormData {
  email: string;
  fullName: string;
  company: string;
  phone: string;
  dealVolume: DealVolume | '';
  investorType: InvestorType | '';
  teamSize: TeamSize | '';
  selectedPlan: 'Core' | 'Scale' | '';
  smsConsent: boolean;
}
interface MultiStepFormProps {
  onSuccess: () => void;
}
/**
 * MultiStepForm component - Beta Version
 * Updated flow for beta signup with $199 fee
 * Step 1: Email only
 * Step 2-4: Questionnaire (deal volume, investor type, team size)
 * Step 3: Plan recommendation with lifetime savings
 * Step 4: Account details + payment
 */
export function MultiStepForm({
  onSuccess
}: MultiStepFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [questionnaireStep, setQuestionnaireStep] = useState(1);
  const [recommendation, setRecommendation] = useState<RecommendationResult | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    fullName: '',
    company: '',
    phone: '',
    dealVolume: '',
    investorType: '',
    teamSize: '',
    selectedPlan: '',
    smsConsent: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const steps = [{
    number: 1,
    label: 'Email'
  }, {
    number: 2,
    label: 'Profile'
  }, {
    number: 3,
    label: 'Plan'
  }, {
    number: 4,
    label: 'Payment'
  }];
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (step === 1) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      }
      if (!formData.smsConsent) {
        newErrors.smsConsent = 'You must consent to receive SMS notifications';
      }
    }
    if (step === 4) {
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
      }
      if (!formData.company.trim()) {
        newErrors.company = 'Company name is required';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleQuestionnaireChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleQuestionnaireNext = () => {
    if (questionnaireStep < 3) {
      setQuestionnaireStep(questionnaireStep + 1);
    } else {
      // Generate recommendation
      const result = recommendPlan({
        dealVolume: formData.dealVolume as DealVolume,
        investorType: formData.investorType as InvestorType,
        teamSize: formData.teamSize as TeamSize
      });
      setRecommendation(result);
      setCurrentStep(3); // Move to recommendation step
    }
  };
  const handleQuestionnaireBack = () => {
    if (questionnaireStep > 1) {
      setQuestionnaireStep(questionnaireStep - 1);
    } else {
      setCurrentStep(1); // Back to email
    }
  };
  const handlePlanSelect = (planName: 'Core' | 'Scale') => {
    setFormData(prev => ({
      ...prev,
      selectedPlan: planName
    }));
    setCurrentStep(4); // Move to details step
  };
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 1) {
        setCurrentStep(2); // Move to questionnaire
      } else if (currentStep === 4) {
        handleSubmit();
      }
    }
  };
  const handleBack = () => {
    if (currentStep === 4) {
      setCurrentStep(3); // Back to recommendation
    } else if (currentStep === 3) {
      setQuestionnaireStep(3); // Back to last question
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(1); // Back to email
    }
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Beta Stripe checkout URL - single payment link for all beta signups
    const betaCheckoutUrl = 'https://buy.stripe.com/aFabJ34DL2uZcvWcWL9R600';
    try {
      await fetch('/api/beta-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      const checkoutUrl = `${betaCheckoutUrl}?prefilled_email=${encodeURIComponent(formData.email)}`;
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error during beta signup:', error);
      setIsSubmitting(false);
      // Fallback to Stripe checkout even if API fails
      const checkoutUrl = `${betaCheckoutUrl}?prefilled_email=${encodeURIComponent(formData.email)}`;
      window.location.href = checkoutUrl;
    }
  };
  return <div data-id="element-2281">
      <ProgressSteps currentStep={currentStep} steps={steps} data-id="element-2282" />

      {/* Step 1: Email + Phone + SMS Consent */}
      {currentStep === 1 && <div className="space-y-6 animate-fade-in" data-id="element-2283">
          <div className="text-center mb-6" data-id="element-2284">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2285">
              Join the beta
            </h3>
            <p className="text-gray-400" data-id="element-2286">
              $199 one-time • 45-day trial • Lifetime 50% discount
            </p>
          </div>

          <div data-id="element-2287">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2288">
              Work Email *
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`block w-full px-4 py-3 bg-[#1a1a1a] border ${errors.email ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all`} placeholder="you@company.com" autoFocus data-id="element-2289" />
            {errors.email && <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-id="element-2290">
                <XCircle className="w-4 h-4" data-id="element-2291" />
                {errors.email}
              </p>}
          </div>

          <div data-id="element-2292">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2293">
              Phone Number *
            </label>
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={`block w-full px-4 py-3 bg-[#1a1a1a] border ${errors.phone ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all`} placeholder="(555) 123-4567" data-id="element-2294" />
            {errors.phone && <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-id="element-2295">
                <XCircle className="w-4 h-4" data-id="element-2296" />
                {errors.phone}
              </p>}
          </div>

          {/* 10DLC Compliant SMS Consent */}
          <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-4" data-id="element-2297">
            <label className="flex items-start gap-3 cursor-pointer group" data-id="element-2298">
              <div className="relative flex items-center justify-center mt-0.5" data-id="element-2299">
                <input type="checkbox" checked={formData.smsConsent} onChange={e => setFormData(prev => ({
              ...prev,
              smsConsent: e.target.checked
            }))} className="w-5 h-5 bg-[#0f0f0f] border-2 border-gray-600 rounded cursor-pointer appearance-none checked:bg-[#3ab795] checked:border-[#3ab795] transition-all focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]" data-id="element-2300" />
                {formData.smsConsent && <Check className="w-3 h-3 text-white absolute pointer-events-none" strokeWidth={3} data-id="element-2301" />}
              </div>
              <div className="flex-1" data-id="element-2302">
                <p className="text-sm text-gray-300 leading-relaxed" data-id="element-2303">
                  I agree to receive SMS notifications from AcquireFlow about my
                  beta access, setup calls, and important product updates.{' '}
                  <span className="text-gray-400" data-id="element-2304">
                    Message frequency varies. Message and data rates may apply.
                    Reply STOP to opt out or HELP for help.
                  </span>
                </p>
              </div>
            </label>
            {errors.smsConsent && <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-id="element-2305">
                <XCircle className="w-4 h-4" data-id="element-2306" />
                {errors.smsConsent}
              </p>}
          </div>

          {/* Terms & Privacy Policy Links */}
          <div className="text-center text-xs text-gray-400" data-id="element-2307">
            By continuing, you agree to our{' '}
            <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-[#3ab795] hover:text-[#2d9f7e] underline transition-colors" data-id="element-2308">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#3ab795] hover:text-[#2d9f7e] underline transition-colors" data-id="element-2309">
              Privacy Policy
            </a>
            .
          </div>

          <button onClick={handleNext} className="w-full px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-lg hover:bg-[#2d9f7e] transition-all duration-300 flex items-center justify-center gap-2 group" data-id="element-2310">
            <span data-id="element-2311">Continue</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2312" />
          </button>
        </div>}

      {/* Step 2: Questionnaire */}
      {currentStep === 2 && <PlanQuestionnaire answers={{
      dealVolume: formData.dealVolume,
      investorType: formData.investorType,
      teamSize: formData.teamSize
    }} currentQuestion={questionnaireStep} onAnswerChange={handleQuestionnaireChange} onNext={handleQuestionnaireNext} onBack={handleQuestionnaireBack} data-id="element-2313" />}

      {/* Step 3: Plan Recommendation with Lifetime Savings */}
      {currentStep === 3 && recommendation && <div className="space-y-6 animate-fade-in" data-id="element-2314">
          <div className="text-center mb-6" data-id="element-2315">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-4" data-id="element-2316">
              <Crown className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2317" />
              <span className="text-sm font-medium text-[#3ab795]" data-id="element-2318">
                Beta Lifetime Pricing
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2319">
              Choose your plan
            </h3>
            <p className="text-gray-400" data-id="element-2320">
              Lock in 50% off for life with your beta access
            </p>
          </div>

          <PlanRecommendation recommendation={recommendation} onSelectPlan={handlePlanSelect} onBack={() => {
        setQuestionnaireStep(3);
        setCurrentStep(2);
      }} data-id="element-2321" />
        </div>}

      {/* Step 4: Account Details + Payment */}
      {currentStep === 4 && <div className="space-y-6 animate-fade-in" data-id="element-2322">
          <div className="text-center mb-6" data-id="element-2323">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2324">
              Complete your profile
            </h3>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-lg" data-id="element-2325">
              <TrendingUp className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2326" />
              <span className="text-sm text-[#3ab795]" data-id="element-2327">
                {formData.selectedPlan} Plan • Lifetime 50% discount
              </span>
            </div>
          </div>

          <div data-id="element-2328">
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2329">
              Full Name *
            </label>
            <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} className={`block w-full px-4 py-3 bg-[#1a1a1a] border ${errors.fullName ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all`} placeholder="John Doe" autoFocus data-id="element-2330" />
            {errors.fullName && <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-id="element-2331">
                <XCircle className="w-4 h-4" data-id="element-2332" />
                {errors.fullName}
              </p>}
          </div>

          <div data-id="element-2333">
            <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-2334">
              Company Name *
            </label>
            <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={`block w-full px-4 py-3 bg-[#1a1a1a] border ${errors.company ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all`} placeholder="Your Company" data-id="element-2335" />
            {errors.company && <p className="mt-2 text-sm text-red-500 flex items-center gap-1" data-id="element-2336">
                <XCircle className="w-4 h-4" data-id="element-2337" />
                {errors.company}
              </p>}
          </div>

          {/* Payment Summary */}
          <div className="bg-[#3ab795]/5 border border-[#3ab795]/20 rounded-lg p-4" data-id="element-2338">
            <div className="flex items-center justify-between mb-2" data-id="element-2339">
              <span className="text-gray-300" data-id="element-2340">Beta Access Fee</span>
              <span className="text-white font-semibold" data-id="element-2341">$199</span>
            </div>
            <div className="text-xs text-gray-400 pt-2 border-t border-[#3ab795]/20" data-id="element-2342">
              One-time payment • 45-day trial • Lifetime 50% discount • Free
              setup call
            </div>
          </div>

          <div className="flex gap-3" data-id="element-2343">
            <button onClick={handleBack} disabled={isSubmitting} className="flex-1 px-6 py-4 bg-[#1a1a1a] border border-gray-700 text-white font-semibold rounded-lg hover:border-[#3ab795] transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2344">
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-2345" />
              <span data-id="element-2346">Back</span>
            </button>
            <button onClick={handleNext} disabled={isSubmitting} className="flex-1 px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-lg hover:bg-[#2d9f7e] transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2347">
              {isSubmitting ? <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-2348">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2349"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-2350"></path>
                  </svg>
                  <span data-id="element-2351">Processing...</span>
                </> : <>
                  <span data-id="element-2352">Pay $199 & Join Beta</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2353" />
                </>}
            </button>
          </div>
        </div>}
    </div>;
}